import EditDialog from "@/components/common/EditDialog";
import NameForm from "@/features/profile/components/NameForm";
import { services as s } from "@/services";
import type { ProfileT } from "@patient-watch/types";
import { Copy } from "../../../components/common/Copy";
import type { TitleT } from "@patient-watch/types";
import ClinicianNumberForm from "./ClinicianNumberForm";
import DateOfBirthForm from "./DateOfBirthForm";
import NhsNumberForm from "./NhsNumberForm";
import PhoneForm from "./PhoneForm";
import UpdateEmailForm from "./UpdateEmailForm";
import UpdatePhoneForm from "./UpdatePhoneForm";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { ui } from "@patient-watch/ui";
import { cn } from "@/lib/utils";
import { EditPencil } from "@patient-watch/ui/edit-pencil";
import { useState } from "react";
import RequiredInfo from "@/components/common/RequiredInfo";
import { useProfileCopy } from "@/features/i18n/profileCopy";

// Helper to format phone number for display
const formatPhone = (phone: string | null, emptyLabel: string): string => {
    if (!phone) return emptyLabel;
    // Add + prefix if not present for formatting
    const phoneWithPlus = phone.startsWith("+") ? phone : `+${phone}`;
    const formatted = formatPhoneNumberIntl(phoneWithPlus);
    return formatted || phone;
};

interface Props {
    profile: ProfileT;
    locked?: boolean;
    show?: boolean;
    editable?: boolean;
}

export const UserIdDialog = ({ profile, locked = true, show = true, editable = true }: Props) => {
    const copy = useProfileCopy();

    return (
        <EditDialog
            noInfo={!profile?.user_id}
            editable={editable}
            locked={locked}
            label="User ID"
            info={
                <Copy id={`copy-${profile.user_id}`} value={profile.user_id ?? copy.noName}>
                    {profile.user_id ?? copy.noName}
                </Copy>
            }
            show={show}
        ></EditDialog>
    );
};

export const NameDialog = ({ profile, locked = false, show = true, editable = true }: Props) => {
    const copy = useProfileCopy();

    return (
        <EditDialog
            noInfo={!profile?.title || !profile?.first_name || !profile?.last_name}
            editable={editable}
            locked={locked}
            label={copy.name}
            triggerTestId="name-trigger"
            info={
                <Copy id={`copy-${profile.name}`} value={profile.name ?? copy.noName}>
                    {profile.name ?? copy.noName}
                </Copy>
            }
            show={show}
        >
            <NameForm
                isDialog={true}
                userId={profile.user_id}
                firstName={profile.first_name}
                title={profile.title as TitleT}
                lastName={profile.last_name}
            />
        </EditDialog>
    );
};

export const DateOfBirthDialog = ({ profile, locked = false, show = true, editable = true }: Props) => {
    const copy = useProfileCopy();

    return (
        <EditDialog
            editable={editable}
            locked={locked}
            label={copy.dateOfBirth}
            triggerTestId="date-of-birth-trigger"
            noInfo={!profile?.date_of_birth}
            info={
                <Copy id={`copy-${profile.date_of_birth}`} value={profile.date_of_birth ?? copy.noDateOfBirth}>
                    {s.time.formatDate(profile.date_of_birth) ?? copy.noDateOfBirth}
                </Copy>
            }
            show={show}
        >
            <DateOfBirthForm isDialog={true} userId={profile.user_id} dateOfBirth={profile.date_of_birth} />
        </EditDialog>
    );
};
export const EmailDialog = ({ profile, locked = false, show = true, editable = true }: Props) => {
    const [open, setOpen] = useState(false);
    const copy = useProfileCopy();

    if (!show) return null;

    if (editable === false) {
        return (
            <RequiredInfo
                label={copy.email}
                info={
                    <Copy id={`copy-${profile.email}`} value={profile.email ?? copy.noEmail}>
                        {profile.email ?? copy.noEmail}
                    </Copy>
                }
                noInfo={!profile.email}
            />
        );
    }

    return (
        <ui.Dialog open={open} onOpenChange={setOpen}>
            <button
                onClick={() => !locked && setOpen(true)}
                type="button"
                className={cn(
                    "flex justify-between items-center m-0 p-0 w-full max-w-[94vw] overflow-clip",
                    locked ? "cursor-text" : "cursor-pointer"
                )}
                data-testid="email-trigger"
            >
                <RequiredInfo
                    label={copy.email}
                    info={
                        <Copy id={`copy-${profile.email}`} value={profile.email ?? copy.noEmail}>
                            {profile.email ?? copy.noEmail}
                        </Copy>
                    }
                    noInfo={!profile.email}
                />
                <EditPencil locked={locked} />
            </button>
            <ui.DialogContent>
                <ui.DialogHeader>
                    <ui.DialogTitle className="text-slate-900 dark:text-slate-100">{copy.updateEmail}</ui.DialogTitle>
                </ui.DialogHeader>
                <ui.ScrollArea className="md:p-2 lg:p-4 max-h-[80vh]">
                    <UpdateEmailForm
                        userId={profile.user_id}
                        currentEmail={profile.email}
                        onSuccess={() => setOpen(false)}
                    />
                    <ui.ScrollBar />
                </ui.ScrollArea>
            </ui.DialogContent>
        </ui.Dialog>
    );
};

export const AdminEmailDialog = ({ profile, locked = false, show = true, editable = true }: Props) => {
    const [open, setOpen] = useState(false);
    const copy = useProfileCopy();

    if (!show) return null;

    if (editable === false) {
        return (
            <RequiredInfo
                label={copy.email}
                info={
                    <Copy id={`copy-${profile.email}`} value={profile.email ?? copy.noEmail}>
                        {profile.email ?? copy.noEmail}
                    </Copy>
                }
                noInfo={!profile.email}
            />
        );
    }

    return (
        <ui.Dialog open={open} onOpenChange={setOpen}>
            <button
                onClick={() => !locked && setOpen(true)}
                type="button"
                className={cn(
                    "flex justify-between items-center m-0 p-0 w-full max-w-[94vw] overflow-clip",
                    locked ? "cursor-text" : "cursor-pointer"
                )}
                data-testid="email-trigger"
            >
                <RequiredInfo
                    label={copy.email}
                    info={
                        <Copy id={`copy-${profile.email}`} value={profile.email ?? copy.noEmail}>
                            {profile.email ?? copy.noEmail}
                        </Copy>
                    }
                    noInfo={!profile.email}
                />
                <EditPencil />
            </button>
            <ui.DialogContent>
                <ui.DialogHeader>
                    <ui.DialogTitle className="text-slate-900 dark:text-slate-100">{copy.updateEmail}</ui.DialogTitle>
                </ui.DialogHeader>
                <ui.ScrollArea className="md:p-2 lg:p-4 max-h-[80vh]">
                    <UpdateEmailForm
                        allowAuthEmailOverride
                        userId={profile.user_id}
                        currentEmail={profile.email}
                        onSuccess={() => setOpen(false)}
                    />
                    <ui.ScrollBar />
                </ui.ScrollArea>
            </ui.DialogContent>
        </ui.Dialog>
    );
};

export const PhoneDialog = ({ profile, locked = false, show = true, editable = true }: Props) => {
    const copy = useProfileCopy();
    const formattedPhone = formatPhone(profile.phone, copy.noPhoneNumber);

    return (
        <EditDialog
            editable={editable}
            info={
                <Copy id={`copy-${profile.phone}`} value={profile.phone ?? copy.noPhoneNumber}>
                    {formattedPhone}
                </Copy>
            }
            locked={locked}
            label={copy.phone}
            noInfo={!profile.phone}
            show={show}
        >
            <PhoneForm isDialog={true} userId={profile.user_id} phone={profile.phone} />
        </EditDialog>
    );
};

export const AdminPhoneDialog = ({ profile, locked = false, show = true, editable = true }: Props) => {
    const [open, setOpen] = useState(false);
    const copy = useProfileCopy();
    const formattedPhone = formatPhone(profile.phone, copy.noPhoneNumber);

    if (!show) return null;

    if (editable === false) {
        return (
            <RequiredInfo
                label={copy.phone}
                info={
                    <Copy id={`copy-${profile.phone}`} value={profile.phone ?? copy.noPhoneNumber}>
                        {formattedPhone}
                    </Copy>
                }
                noInfo={!profile.phone}
            />
        );
    }

    return (
        <ui.Dialog open={open} onOpenChange={setOpen}>
            <button
                onClick={() => !locked && setOpen(true)}
                type="button"
                className={cn(
                    "flex justify-between items-center m-0 p-0 w-full max-w-[94vw] overflow-clip",
                    locked ? "cursor-text" : "cursor-pointer"
                )}
                data-testid="phone-trigger"
            >
                <RequiredInfo
                    label={copy.phone}
                    info={
                        <Copy id={`copy-${profile.phone}`} value={profile.phone ?? copy.noPhoneNumber}>
                            {formattedPhone}
                        </Copy>
                    }
                    noInfo={!profile.phone}
                />
                <EditPencil />
            </button>
            <ui.DialogContent>
                <ui.DialogHeader>
                    <ui.DialogTitle className="text-slate-900 dark:text-slate-100">Update Phone</ui.DialogTitle>
                </ui.DialogHeader>
                <ui.ScrollArea className="md:p-2 lg:p-4 max-h-[80vh]">
                    <UpdatePhoneForm
                        userId={profile.user_id}
                        currentPhone={profile.phone}
                        onSuccess={() => setOpen(false)}
                    />
                    <ui.ScrollBar />
                </ui.ScrollArea>
            </ui.DialogContent>
        </ui.Dialog>
    );
};

export const NhsNumberDialog = ({ profile, locked = false, show = true, editable = true }: Props) => {
    const nhsNumber = profile.nhs_number != null ? String(profile.nhs_number) : null;

    return (
        <EditDialog
            editable={editable}
            locked={locked}
            label="NHS Number"
            show={show}
            info={
                nhsNumber ? (
                    <Copy id={`copy-${profile.nhs_number}`} value={nhsNumber}>
                        {nhsNumber}
                    </Copy>
                ) : (
                    "No NHS number"
                )
            }
            noInfo={!profile.nhs_number}
        >
            <NhsNumberForm isDialog={true} userId={profile.user_id} nhsNumber={profile.nhs_number} />
        </EditDialog>
    );
};

export const ClinicianNumberDialog = ({ profile, locked = false, show = true, editable = true }: Props) => {
    const copy = useProfileCopy();
    return (
        <EditDialog
            editable={editable}
            locked={locked}
            label={copy.clinicianNumberLabel}
            description={copy.clinicianNumberDescription}
            show={show}
            info={
                <Copy
                    id={`copy-${profile.clinician_number}`}
                    value={profile.clinician_number != null ? String(profile.clinician_number) : copy.noClinicianNumber}
                >
                    {profile.clinician_number ?? copy.noClinicianNumber}
                </Copy>
            }
            noInfo={!profile.clinician_number}
        >
            <ClinicianNumberForm isDialog={true} userId={profile.user_id} clinicianNumber={profile.clinician_number} />
        </EditDialog>
    );
};
